import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Send, Paperclip, X, FileText, Check, CheckCheck, Star, Reply, Trash2, Edit3, Copy, Download, MoreHorizontal, Forward, Search, Lock } from 'lucide-react';
import api, { API, mediaUrl } from '../lib/api';
import { useWS } from '../context/WSContext';
import { useAuth } from '../context/AuthContext';
import { useE2E } from '../context/E2EContext';
import { useLightbox } from '../context/LightboxContext';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import Avatar from './Avatar';
import { formatLastSeen } from '../lib/time';
import ChatImageCropper from './ChatImageCropper';
import AudioBubble from './chat/AudioBubble';
import MediaBubble from './chat/MediaBubble';
import { formatTime, formatDay, useLongPress } from './chat/chatUtils';

/**
 * Quoted-reply preview shown inside a message bubble.
 */
function ReplyQuote({ reply, isOut }) {
  if (!reply) return null;
  const preview = reply.type === 'text'
    ? (reply.content || '')
    : {
        image: '📷 Foto',
        video: '🎥 Video',
        audio: '🎤 Nota vocale',
        file: `📎 ${reply.file_name || 'File'}`,
      }[reply.type] || 'Messaggio';
  return (
    <div className={`mb-1.5 border-l-2 rounded-md px-2 py-1 text-[11px] ${isOut ? 'border-black/40 bg-black/10' : 'border-[var(--gold)] bg-[var(--gold)]/8'}`}>
      <div className="font-semibold opacity-80 truncate">{reply.sender_username || 'Messaggio'}</div>
      <div className="opacity-70 truncate">{preview}</div>
    </div>
  );
}

/**
 * Bottom sheet action menu on long-press / right-click.
 */
function ActionSheet({ open, message, isOwn, isAdmin, onClose, onReply, onEdit, onDelete, onStar, onCopy, onSave, onForward }) {
  if (!open || !message) return null;
  const isImage = message.type === 'image';
  const isText = message.type === 'text';
  const starred = !!message._isStarred;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9997] bg-black/60 flex items-end justify-center"
      onClick={onClose}
      data-testid="chat-actionsheet"
    >
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        exit={{ y: 100 }}
        transition={{ type: 'spring', damping: 24 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[var(--bg-surface)] border-t border-[var(--border-subtle)] rounded-t-3xl p-2 pb-6 safe-bottom"
      >
        <div className="w-10 h-1 rounded-full bg-white/20 mx-auto my-2" />
        <ActionItem icon={Reply} label="Rispondi" onClick={onReply} testid="action-reply" />
        {isAdmin && <ActionItem icon={Forward} label="Inoltra" onClick={onForward} testid="action-forward" />}
        <ActionItem icon={Star} label={starred ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'} onClick={onStar} testid="action-star" gold={starred} />
        {isText && <ActionItem icon={Copy} label="Copia testo" onClick={onCopy} testid="action-copy" />}
        {isImage && <ActionItem icon={Download} label="Salva in galleria" onClick={onSave} testid="action-save-gallery" />}
        {isOwn && isText && <ActionItem icon={Edit3} label="Modifica" onClick={onEdit} testid="action-edit" />}
        {isOwn && <ActionItem icon={Trash2} label="Elimina per tutti" onClick={onDelete} testid="action-delete" danger />}
      </motion.div>
    </motion.div>
  );
}

function ActionItem({ icon: Icon, label, onClick, danger, gold, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-4 px-4 py-3 rounded-2xl text-left hover:bg-white/5 transition-colors ${danger ? 'text-red-400' : gold ? 'text-[var(--gold)]' : 'text-white/90'}`}
    >
      <Icon className="w-5 h-5" />
      <span className="text-sm">{label}</span>
    </button>
  );
}

/**
 * Composer draft displayed above the input when replying to or editing a message.
 */
function DraftPreview({ mode, targetMsg, onCancel }) {
  if (!targetMsg) return null;
  const label = mode === 'edit' ? 'Modifica messaggio' : `Rispondi a ${targetMsg.sender_username || ''}`;
  const preview = targetMsg.type === 'text'
    ? (targetMsg.content || '').slice(0, 100)
    : ({ image: '📷 Foto', video: '🎥 Video', audio: '🎤 Nota vocale', file: `📎 ${targetMsg.file_name || 'File'}` }[targetMsg.type] || 'Messaggio');
  return (
    <div className="mb-2 flex items-center gap-2 border-l-2 border-[var(--gold)] pl-2 pr-1 py-1 bg-[var(--gold)]/8 rounded" data-testid="chat-draft-preview">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[var(--gold)]">{label}</div>
        <div className="text-xs text-white/70 truncate">{preview}</div>
      </div>
      <button onClick={onCancel} className="p-1 text-white/60 hover:text-white" data-testid="draft-cancel">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function ChatView({ recipientId, recipientName, recipientOnline, recipientLastSeen, recipientAvatarUrl }) {
  const { user } = useAuth();
  const { subscribe, send, presence, connected } = useWS();
  const e2e = useE2E();
  const { open: openImage } = useLightbox();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // full msg object
  const [editing, setEditing] = useState(null); // msg being edited
  const [sheetFor, setSheetFor] = useState(null); // msg selected for actions
  const [pendingImage, setPendingImage] = useState(null); // { src, file } for cropper
  const [forwardFor, setForwardFor] = useState(null); // msg to forward
  const [forwardTargets, setForwardTargets] = useState([]); // list of admin users
  const [forwardSearch, setForwardSearch] = useState('');
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const [starredOpen, setStarredOpen] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const [peerHasE2E, setPeerHasE2E] = useState(false);

  const enrichMessage = (m) => ({ ...m, _isStarred: (m.starred_by || []).includes(user.id) });

  // Detect whether the peer (and self) both have E2E keys → enable outbound E2E
  useEffect(() => {
    if (!e2e?.ready || !recipientId) { setPeerHasE2E(false); return; }
    e2e.peerHasKeys(recipientId).then(setPeerHasE2E).catch(() => setPeerHasE2E(false));
  }, [e2e, e2e?.ready, recipientId]);

  const decryptIncomingIfNeeded = useCallback(async (m) => {
    if (!m.encrypted || !m.crypto) return m;
    if (m.type === 'text' && e2e?.ready) {
      try {
        const peer = m.sender_id === user.id ? m.recipient_id : m.sender_id;
        const plain = await e2e.decryptFromPeer(peer, m.crypto);
        return { ...m, content: plain };
      } catch {
        return { ...m, content: '🔒 Messaggio cifrato (non decifrabile)' };
      }
    }
    return m;
  }, [e2e, user.id]);

  const load = async () => {
    const params = user.is_admin ? { with_user: recipientId } : {};
    const { data } = await api.get('/messages', { params });
    const decrypted = await Promise.all(data.map(decryptIncomingIfNeeded));
    const enriched = decrypted.map(enrichMessage);
    // Merge with any WS-received messages that arrived while this HTTP request
    // was in flight (they'd otherwise be overwritten and lost until page reload).
    // Also preserve pending "ghost" bubbles and any locally-cached blob URLs.
    setMessages((prev) => {
      const prevById = new Map(prev.map((m) => [m.id, m]));
      const serverIds = new Set(enriched.map((m) => m.id));
      const withPreserved = enriched.map((m) => {
        const p = prevById.get(m.id);
        return p && p._localBlobUrl ? { ...m, _localBlobUrl: p._localBlobUrl } : m;
      });
      const inFlight = prev.filter((m) => !serverIds.has(m.id));
      if (inFlight.length === 0) return withPreserved;
      const merged = [...withPreserved, ...inFlight];
      merged.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      return merged;
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [recipientId, e2e?.ready]);

  // Re-sync messages when the WebSocket reconnects (mobile background/network switches
  // silently drop messages during the outage) and when the tab becomes visible again.
  const wasConnectedRef = useRef(connected);
  useEffect(() => {
    if (connected && !wasConnectedRef.current) {
      // Reconnected → catch up on anything missed while the socket was down.
      load();
    }
    wasConnectedRef.current = connected;
    // eslint-disable-next-line
  }, [connected]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line
  }, [recipientId, e2e?.ready]);

  useEffect(() => {
    const unsub = subscribe(async (data) => {
      if (data.type === 'message') {
        let m = data.message;
        m = await decryptIncomingIfNeeded(m);
        m = enrichMessage(m);
        const inChat = (m.sender_id === recipientId && m.recipient_id === user.id)
          || (m.sender_id === user.id && m.recipient_id === recipientId);
        if (inChat) {
          setMessages((prev) => {
            if (prev.some((x) => x.id === m.id)) return prev;
            // If it's our own send and a pending ghost is still waiting for its real id,
            // skip: the POST response will swap the ghost (and preserve _localBlobUrl).
            if (m.sender_id === user.id && prev.some((x) => x._pending && x.type === m.type && x.file_name === m.file_name)) {
              return prev;
            }
            return [...prev, m];
          });
          if (m.sender_id === recipientId && document.hidden && Notification.permission === 'granted') {
            new Notification(m.sender_username || 'Nuovo messaggio', { body: m.content || 'Media' });
          }
        }
      } else if (data.type === 'message_edited') {
        // If edited message is encrypted, decrypt via crypto payload before applying
        if (data.encrypted && data.crypto && e2e?.ready) {
          try {
            const peer = recipientId; // in-chat, peer is fixed
            const plain = await e2e.decryptFromPeer(peer, data.crypto);
            setMessages((prev) => prev.map((mm) => mm.id === data.message_id ? { ...mm, content: plain, edited_at: data.edited_at, encrypted: true, crypto: data.crypto } : mm));
          } catch {
            setMessages((prev) => prev.map((mm) => mm.id === data.message_id ? { ...mm, content: '🔒 Cifrato', edited_at: data.edited_at } : mm));
          }
        } else {
          setMessages((prev) => prev.map((mm) => mm.id === data.message_id ? { ...mm, content: data.content, edited_at: data.edited_at } : mm));
        }
      } else if (data.type === 'message_deleted') {
        setMessages((prev) => prev.map((mm) => mm.id === data.message_id ? { ...mm, deleted_for_all: true, content: '', file_url: null } : mm));
      } else if (data.type === 'read_receipt' && data.by === recipientId) {
        setMessages((prev) => prev.map(mm => mm.sender_id === user.id ? { ...mm, read: true } : mm));
      } else if (data.type === 'typing' && data.from === recipientId) {
        setTyping(true);
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setTyping(false), 3000);
      }
    });
    return unsub;
    // eslint-disable-next-line
  }, [recipientId, user.id, e2e, decryptIncomingIfNeeded]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, typing]);

  const canE2E = () => !!(e2e?.ready && peerHasE2E);

  const sendText = async () => {
    if (!text.trim()) return;
    // Edit mode?
    if (editing) {
      try {
        let payload;
        if (canE2E()) {
          const crypto = await e2e.encryptForPeer(recipientId, text.trim());
          payload = { content: '', encrypted: true, crypto };
        } else {
          payload = { content: text.trim() };
        }
        await api.patch(`/messages/${editing.id}`, payload);
        setMessages((prev) => prev.map((m) => m.id === editing.id ? { ...m, content: text.trim(), edited_at: new Date().toISOString() } : m));
        setEditing(null);
        setText('');
      } catch { toast.error('Modifica fallita'); }
      return;
    }
    // Normal send
    const plain = text.trim();
    let payload;
    if (canE2E()) {
      try {
        const crypto = await e2e.encryptForPeer(recipientId, plain);
        payload = { content: '', type: 'text', encrypted: true, crypto };
      } catch {
        payload = { content: plain, type: 'text' };
      }
    } else {
      payload = { content: plain, type: 'text' };
    }
    if (user.is_admin) payload.recipient_id = recipientId;
    if (replyTo) payload.reply_to = replyTo.id;
    try {
      const { data: msg } = await api.post('/messages', payload);
      // Server returns "" for encrypted; keep the plaintext we already know
      if (msg.encrypted) msg.content = plain;
      setMessages((prev) => prev.some(x => x.id === msg.id) ? prev : [...prev, enrichMessage(msg)]);
      setText('');
      setReplyTo(null);
    } catch { toast.error('Invio fallito'); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const tempId = 'pending-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
    const localBlobUrl = URL.createObjectURL(file);
    const type = file.type.startsWith('image/') ? 'image'
      : file.type.startsWith('video/') ? 'video'
      : file.type.startsWith('audio/') ? 'audio'
      : 'file';
    // Optimistic "ghost" bubble so the user sees the image immediately with a progress overlay
    const ghostMsg = enrichMessage({
      id: tempId,
      sender_id: user.id,
      recipient_id: recipientId,
      type,
      content: '',
      file_name: file.name,
      file_type: file.type,
      created_at: new Date().toISOString(),
      _localBlobUrl: localBlobUrl,
      _uploadProgress: 0,
      _pending: true,
    });
    setMessages((prev) => [...prev, ghostMsg]);
    try {
      let cryptoMeta = null;
      let blobToUpload = file;
      let uploadFilename = file.name;

      if (canE2E()) {
        try {
          const { cipherBlob, crypto } = await e2e.encryptFileForPeer(recipientId, file);
          blobToUpload = new File([cipherBlob], `${file.name}.enc`, { type: 'application/octet-stream' });
          uploadFilename = `${file.name}.enc`;
          cryptoMeta = crypto;
        } catch (e) {
          console.warn('[E2E file encrypt failed, sending plaintext]', e);
        }
      }

      const form = new FormData();
      form.append('file', blobToUpload, uploadFilename);
      const { data } = await api.post('/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.min(99, Math.round((evt.loaded / evt.total) * 100));
          setMessages((prev) => prev.map((m) => m.id === tempId ? { ...m, _uploadProgress: pct } : m));
        },
      });
      const payload = {
        type, content: '',
        file_url: `/api/files/${data.file_id}`,
        file_name: file.name,
        file_type: file.type,
      };
      if (cryptoMeta) {
        payload.encrypted = true;
        payload.crypto = cryptoMeta;
      }
      if (user.is_admin) payload.recipient_id = recipientId;
      if (replyTo) payload.reply_to = replyTo.id;
      const { data: msg } = await api.post('/messages', payload);
      // Replace ghost with real message. Preserve the local blob URL so the sender
      // sees the picture instantly (no need to download + decrypt what we just uploaded).
      // Handle the race where the WS "message" echo arrived before the POST resolved.
      setMessages((prev) => {
        const alreadyReal = prev.some((m) => m.id === msg.id);
        if (alreadyReal) {
          return prev
            .filter((m) => m.id !== tempId)
            .map((m) => m.id === msg.id
              ? enrichMessage({ ...m, _localBlobUrl: localBlobUrl })
              : m);
        }
        return prev.map((m) => m.id === tempId
          ? enrichMessage({ ...msg, _localBlobUrl: localBlobUrl })
          : m);
      });
      setReplyTo(null);
    } catch {
      setMessages((prev) => prev.map((m) => m.id === tempId
        ? { ...m, _failed: true, _uploadProgress: 0 }
        : m));
      toast.error('Invio foto fallito. Tocca per riprovare.');
    }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // If image → open cropper first
    if (file.type.startsWith('image/')) {
      const src = URL.createObjectURL(file);
      setPendingImage({ src, name: file.name });
    } else {
      uploadFile(file);
    }
  };

  const confirmCroppedImage = async (blob) => {
    const file = new File([blob], pendingImage?.name || `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
    setPendingImage(null);
    await uploadFile(file);
  };

  const cancelCrop = () => {
    if (pendingImage?.src) URL.revokeObjectURL(pendingImage.src);
    setPendingImage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleTyping = (v) => {
    setText(v);
    send({ type: 'typing', target: recipientId });
  };

  // Action-sheet callbacks
  const doReply = () => { setReplyTo(sheetFor); setEditing(null); setSheetFor(null); };
  const doEdit = () => { setEditing(sheetFor); setText(sheetFor.content || ''); setReplyTo(null); setSheetFor(null); };
  const doCopy = async () => { try { await navigator.clipboard.writeText(sheetFor.content || ''); toast.success('Testo copiato'); } catch { toast.error('Copia fallita'); } setSheetFor(null); };
  const doDelete = async () => {
    const target = sheetFor;
    setSheetFor(null);
    try {
      await api.delete(`/messages/${target.id}`);
      setMessages((prev) => prev.map((m) => m.id === target.id ? { ...m, deleted_for_all: true, content: '', file_url: null } : m));
      toast.success('Messaggio eliminato');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Eliminazione fallita'); }
  };
  const doStar = async () => {
    const target = sheetFor;
    setSheetFor(null);
    try {
      const { data } = await api.post(`/messages/${target.id}/star`);
      setMessages((prev) => prev.map((m) => {
        if (m.id !== target.id) return m;
        const sb = new Set(m.starred_by || []);
        if (data.starred) sb.add(user.id); else sb.delete(user.id);
        return { ...m, starred_by: Array.from(sb), _isStarred: data.starred };
      }));
      toast.success(data.starred ? 'Aggiunto ai preferiti' : 'Rimosso dai preferiti');
    } catch { toast.error('Errore'); }
  };
  const doSaveGallery = async () => {
    const target = sheetFor;
    setSheetFor(null);
    try {
      const { data: blob } = await axios.get(mediaUrl(target.file_url), { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = target.file_name || `image-${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success('Immagine salvata');
    } catch { toast.error('Salvataggio fallito'); }
  };

  const doForward = async () => {
    if (!sheetFor) return;
    const msg = sheetFor;
    setSheetFor(null);
    setForwardSearch('');
    // Load list of chat targets: admins see all non-admin users; users see admins
    try {
      const { data } = await api.get('/users');
      // Exclude the current recipient and blocked users
      const candidates = data.filter((u) => u.id !== recipientId && !u.is_blocked);
      setForwardTargets(candidates);
      setForwardFor(msg);
    } catch { toast.error('Impossibile caricare la lista'); }
  };

  const forwardTo = async (targetId) => {
    if (!forwardFor) return;
    const msg = forwardFor;
    const payload = {
      type: msg.type,
      content: msg.type === 'text' ? (msg.content || '') : '',
      file_url: msg.file_url || null,
      file_name: msg.file_name || null,
      file_type: msg.file_type || null,
      recipient_id: targetId,
    };
    try {
      await api.post('/messages', payload);
      toast.success('Inoltrato');
    } catch { toast.error('Inoltro fallito'); }
    setForwardFor(null);
  };

  const filteredForwardTargets = useMemo(() => {
    const q = forwardSearch.trim().toLowerCase();
    if (!q) return forwardTargets;
    return forwardTargets.filter((u) => (u.username || '').toLowerCase().includes(q));
  }, [forwardTargets, forwardSearch]);

  const chatStarred = useMemo(
    () => messages.filter((m) => m._isStarred && !m.deleted_for_all),
    [messages],
  );
  const [starredSheetFor, setStarredSheetFor] = useState(null);

  const jumpToMessage = (id) => {
    setStarredOpen(false);
    setStarredSheetFor(null);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="msg-${id}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightId(id);
      setTimeout(() => setHighlightId(null), 1600);
    }, 150);
  };

  const unstarFromSheet = async (msg) => {
    setStarredSheetFor(null);
    try {
      await api.post(`/messages/${msg.id}/star`);
      setMessages((prev) => prev.map((m) => {
        if (m.id !== msg.id) return m;
        const sb = (m.starred_by || []).filter((id) => id !== user.id);
        return { ...m, starred_by: sb, _isStarred: false };
      }));
      toast.success('Rimosso dai preferiti');
    } catch { toast.error('Errore'); }
  };

  const grouped = [];
  let lastDay = null;
  for (const m of messages) {
    const day = formatDay(m.created_at);
    if (day !== lastDay) { grouped.push({ type: 'day', day, id: 'd-' + day }); lastDay = day; }
    grouped.push({ type: 'msg', msg: m });
  }
  const otherPresence = presence[recipientId];
  const isOnline = otherPresence?.online ?? recipientOnline;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {peerHasE2E && e2e?.ready && (
        <div
          data-testid="chat-e2e-banner"
          className="px-4 py-1.5 flex items-center justify-center gap-1.5 text-[11px] text-[var(--gold)] bg-[var(--gold)]/[0.06] border-b border-[var(--gold)]/20"
        >
          <Lock className="w-3 h-3" />
          <span className="tracking-wide">Chat protetta con crittografia end-to-end</span>
        </div>
      )}
      {e2e?.ready && !peerHasE2E && recipientId && (
        <div
          data-testid="chat-e2e-pending-banner"
          className="px-4 py-1.5 flex items-center justify-center gap-1.5 text-[11px] text-white/50 bg-white/[0.03] border-b border-white/10"
        >
          <Lock className="w-3 h-3 opacity-60" />
          <span className="tracking-wide">In attesa che l'utente aggiorni l'app per attivare la crittografia</span>
        </div>
      )}
      <div className="px-5 py-3 border-b border-[var(--border-subtle)] flex items-center gap-3 glass">
        <Avatar username={recipientName} avatarUrl={recipientAvatarUrl} size={40} />
        <div className="flex-1">
          <div className="font-heading font-medium flex items-center gap-1.5">
            {recipientName}
            {peerHasE2E && e2e?.ready && (
              <span title="Chat protetta da crittografia end-to-end" data-testid="chat-e2e-badge">
                <Lock className="w-3.5 h-3.5 text-[var(--gold)]" />
              </span>
            )}
          </div>
          <div className="text-[11px] text-white/50 flex items-center gap-1">
            <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-[var(--success)]' : 'bg-white/30'}`} />
            {isOnline ? 'online' : (typing ? 'sta scrivendo…' : (formatLastSeen(otherPresence?.last_seen || recipientLastSeen) ? `ultimo accesso ${formatLastSeen(otherPresence?.last_seen || recipientLastSeen)}` : 'offline'))}
          </div>
        </div>
        <button
          data-testid="chat-open-starred"
          onClick={() => setStarredOpen(true)}
          aria-label="Preferiti della chat"
          title="Preferiti di questa chat"
          className="p-2 text-[var(--gold)] hover:scale-110 transition-transform"
        >
          <Star className="w-5 h-5" />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2 hide-scrollbar">
        {grouped.length === 0 && (
          <div className="h-full flex items-center justify-center text-white/40 text-sm">
            Nessun messaggio ancora. Scrivi qualcosa!
          </div>
        )}
        {grouped.map((g) => {
          if (g.type === 'day') {
            return (
              <div key={g.id} className="flex justify-center py-2">
                <div className="text-[10px] uppercase tracking-widest text-white/40 bg-[var(--bg-surface)] px-3 py-1 rounded-full border border-[var(--border-subtle)]">
                  {g.day}
                </div>
              </div>
            );
          }
          return <MessageRow key={g.msg.id} msg={g.msg} isOut={g.msg.sender_id === user.id} onLongPress={setSheetFor} onImageClick={openImage} highlighted={highlightId === g.msg.id} />;
        })}
        {typing && (
          <div className="flex justify-start">
            <div className="bubble-in px-3 py-2 text-white/60 text-xs">sta scrivendo…</div>
          </div>
        )}
      </div>

      <div className="glass border-t border-[var(--border-subtle)] px-3 py-3 safe-bottom">
        {(replyTo || editing) && (
          <DraftPreview mode={editing ? 'edit' : 'reply'} targetMsg={editing || replyTo} onCancel={() => { setReplyTo(null); setEditing(null); setText(''); }} />
        )}
        {uploading && <div className="text-xs text-[var(--gold)] mb-2 text-center">Caricamento in corso…</div>}
        <div className="flex items-center gap-2">
          <button
            data-testid="chat-attach-btn"
            onClick={() => fileInputRef.current?.click()}
            className="p-2 text-white/60 hover:text-[var(--gold)] transition-colors"
          >
            <Paperclip className="w-5 h-5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={onFile}
            accept="image/*,video/*,application/pdf,.pdf,.doc,.docx,.txt,.zip"
            data-testid="chat-file-input"
          />
          <input
            data-testid="chat-input"
            type="text"
            value={text}
            onChange={(e) => handleTyping(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendText()}
            placeholder={editing ? 'Modifica messaggio…' : 'Scrivi un messaggio…'}
            className="flex-1 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] focus:border-[var(--gold)]/50 rounded-full px-4 py-2 text-white outline-none text-sm transition-colors"
          />
          <button
            data-testid="chat-send-btn"
            onClick={sendText}
            disabled={!text.trim() && !editing}
            className="w-10 h-10 rounded-full bg-[var(--gold)] text-black flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
          >
            {editing ? <Check className="w-4 h-4" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        <ActionSheet
          key="sheet"
          open={!!sheetFor}
          message={sheetFor}
          isOwn={sheetFor?.sender_id === user.id}
          isAdmin={!!user.is_admin}
          onClose={() => setSheetFor(null)}
          onReply={doReply}
          onEdit={doEdit}
          onDelete={doDelete}
          onStar={doStar}
          onCopy={doCopy}
          onSave={doSaveGallery}
          onForward={doForward}
        />
      </AnimatePresence>

      <AnimatePresence>
        {forwardFor && (
          <motion.div
            key="fwd"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998] bg-black/60 flex items-end justify-center"
            onClick={() => setForwardFor(null)}
            data-testid="forward-sheet"
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              transition={{ type: 'spring', damping: 24 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-[var(--bg-surface)] border-t border-[var(--border-subtle)] rounded-t-3xl p-4 pb-6 safe-bottom max-h-[80vh] flex flex-col"
            >
              <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-3" />
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-base">Inoltra a…</h3>
                <button onClick={() => setForwardFor(null)} className="p-1 text-white/60"><X className="w-4 h-4" /></button>
              </div>
              <div className="relative mb-3">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  data-testid="forward-search"
                  type="text"
                  value={forwardSearch}
                  onChange={(e) => setForwardSearch(e.target.value)}
                  placeholder="Cerca…"
                  className="input-gold pl-9"
                />
              </div>
              <div className="flex-1 overflow-y-auto space-y-1 hide-scrollbar">
                {filteredForwardTargets.length === 0 && (
                  <div className="text-center text-white/40 text-sm py-6" data-testid="forward-empty">
                    Nessun utente disponibile
                  </div>
                )}
                {filteredForwardTargets.map((u) => (
                  <button
                    key={u.id}
                    data-testid={`forward-target-${u.username}`}
                    onClick={() => forwardTo(u.id)}
                    className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors text-left"
                  >
                    <Avatar username={u.username} avatarUrl={u.avatar_url} size={36} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-2">
                        {u.username}
                        {u.is_admin && <span className="text-[8px] bg-[var(--gold)] text-black px-1 py-0.5 rounded-full font-bold tracking-widest">ADMIN</span>}
                      </div>
                    </div>
                    <Forward className="w-4 h-4 text-[var(--gold)]" />
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {starredOpen && (
          <motion.div
            key="chat-starred"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998] bg-black/60 flex items-end justify-center"
            onClick={() => setStarredOpen(false)}
            data-testid="chat-starred-sheet"
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              transition={{ type: 'spring', damping: 24 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-[var(--bg-surface)] border-t border-[var(--border-subtle)] rounded-t-3xl p-4 pb-6 safe-bottom max-h-[75vh] flex flex-col"
            >
              <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-3" />
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-base flex items-center gap-2">
                  <Star className="w-4 h-4 text-[var(--gold)] fill-[var(--gold)]" />
                  Preferiti di {recipientName}
                </h3>
                <button onClick={() => setStarredOpen(false)} className="p-1 text-white/60"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto space-y-2 hide-scrollbar">
                {chatStarred.length === 0 && (
                  <div className="text-center text-white/50 text-sm py-6" data-testid="chat-starred-empty">
                    Nessun messaggio preferito in questa chat.<br />
                    <span className="text-[11px] text-white/40">Tieni premuto un messaggio e tocca ★</span>
                  </div>
                )}
                {chatStarred.map((m) => {
                  const preview = m.type === 'text'
                    ? (m.content || '')
                    : { image: '📷 Foto', video: '🎥 Video', audio: '🎤 Nota vocale', file: `📎 ${m.file_name || 'File'}` }[m.type] || 'Messaggio';
                  return (
                    <StarredItem
                      key={m.id}
                      msg={m}
                      preview={preview}
                      onTap={() => jumpToMessage(m.id)}
                      onLongPress={() => setStarredSheetFor(m)}
                    />
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {starredSheetFor && (
          <motion.div
            key="starred-actions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/60 flex items-end justify-center"
            onClick={() => setStarredSheetFor(null)}
            data-testid="starred-actionsheet"
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              transition={{ type: 'spring', damping: 24 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-[var(--bg-surface)] border-t border-[var(--border-subtle)] rounded-t-3xl p-2 pb-6 safe-bottom"
            >
              <div className="w-10 h-1 rounded-full bg-white/20 mx-auto my-2" />
              <ActionItem
                icon={Reply}
                label="Vai al messaggio"
                onClick={() => jumpToMessage(starredSheetFor.id)}
                testid="action-jump-to-msg"
              />
              <ActionItem
                icon={Star}
                label="Rimuovi dai preferiti"
                onClick={() => unstarFromSheet(starredSheetFor)}
                testid="action-unstar"
                gold
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {pendingImage && (
          <ChatImageCropper
            key="cropper"
            imageSrc={pendingImage.src}
            onCancel={cancelCrop}
            onConfirm={confirmCroppedImage}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function MessageRow({ msg, isOut, onLongPress, onImageClick, highlighted }) {
  const longPress = useLongPress(() => onLongPress(msg));
  const isDeleted = msg.deleted_for_all;
  const isEdited = !!msg.edited_at;
  const starred = msg._isStarred;

  if (isDeleted) {
    return (
      <div className={`flex ${isOut ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${msg.id}`}>
        <div className={`${isOut ? 'bubble-out' : 'bubble-in'} px-3 py-2 max-w-[75%] italic opacity-60`}>
          <div className="text-sm">Messaggio eliminato</div>
          <div className={`flex items-center justify-end gap-1 mt-1 text-[10px] ${isOut ? 'text-black/60' : 'text-white/40'}`}>
            <span>{formatTime(msg.created_at)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 5 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      className={`flex ${isOut ? 'justify-end' : 'justify-start'} ${highlighted ? 'ring-2 ring-[var(--gold)] ring-offset-2 ring-offset-[var(--bg-void)] rounded-2xl transition-shadow' : ''}`}
      data-testid={`msg-${msg.id}`}
    >
      <div
        {...longPress}
        className={`${isOut ? 'bubble-out' : 'bubble-in'} px-3 py-2 max-w-[75%] shadow-sm select-none cursor-pointer`}
        data-testid={`msg-bubble-${msg.id}`}
      >
        <ReplyQuote reply={msg.reply_to} isOut={isOut} />
        {msg.type !== 'text' && <div className="mb-1"><MediaBubble msg={msg} isOut={isOut} onImageClick={onImageClick} /></div>}
        {msg.content && <div className="text-sm whitespace-pre-wrap break-words">{msg.content}</div>}
        <div className={`flex items-center justify-end gap-1 mt-1 text-[10px] ${isOut ? 'text-black/60' : 'text-white/40'}`}>
          {starred && <Star className="w-3 h-3 fill-current" data-testid={`msg-starred-${msg.id}`} />}
          {isEdited && <span className="italic">modificato</span>}
          <span>{formatTime(msg.created_at)}</span>
          {isOut && (msg.read ? <CheckCheck className="w-3 h-3 text-[var(--read-blue)]" /> : <Check className="w-3 h-3" />)}
        </div>
      </div>
    </motion.div>
  );
}

function formatDayTime(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const y = new Date(); y.setDate(now.getDate() - 1);
    const isToday = d.toDateString() === now.toDateString();
    const isYesterday = d.toDateString() === y.toDateString();
    const time = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return `Oggi · ${time}`;
    if (isYesterday) return `Ieri · ${time}`;
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined }) + ' · ' + time;
  } catch { return ''; }
}

function StarredItem({ msg, preview, onTap, onLongPress }) {
  const longPress = useLongPress(onLongPress);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onTap}
      {...longPress}
      data-testid={`chat-starred-item-${msg.id}`}
      className="w-full text-left card-surface p-3 hover:border-[var(--gold)]/30 transition-colors cursor-pointer select-none"
    >
      <div className="text-[10px] text-white/50 mb-1 font-heading tracking-wide">{msg.sender_username || 'Messaggio'}</div>
      <div className="text-sm text-white/90 line-clamp-3 whitespace-pre-wrap break-words">{preview}</div>
      <div className="mt-2 text-[10px] text-white/40 flex items-center gap-1" data-testid={`starred-item-time-${msg.id}`}>
        <Star className="w-3 h-3 text-[var(--gold)] fill-[var(--gold)]" />
        {formatDayTime(msg.created_at)}
      </div>
    </div>
  );
}

